// ═══════════════════════════════════════════════════════════════
// PIXALLOY — Vector Layer Style Dialog
// ═══════════════════════════════════════════════════════════════
// Edit stroke color/width, fill color, opacity for vector layers.
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from "react";
import { V } from "../theme.js";
import { OptBtn, AdjRow, Modal } from "../ui/components.jsx";

/**
 * Props:
 *   open       boolean
 *   layer      layer object (must have type==="vector" and vectorData)
 *   layerIdx   number
 *   onApply    (idx, style) => void
 *   onClose    () => void
 */
export function VectorStyleDialog({ open, layer, layerIdx, onApply, onClose }) {
  const [strokeColor, setStrokeColor] = useState("#000000");
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [fillColor, setFillColor] = useState("");
  const [hasFill, setHasFill] = useState(false);
  const [opacity, setOpacity] = useState(100);

  useEffect(() => {
    if (!open || !layer?.vectorData) return;
    const s = layer.vectorData.style || {};
    setStrokeColor(s.strokeColor || "#000000");
    setStrokeWidth(s.strokeWidth || 2);
    setFillColor(s.fillColor || "#000000");
    setHasFill(!!s.fillColor);
    setOpacity(Math.round((s.opacity ?? 1) * 100));
  }, [open, layer]);

  if (!open) return null;

  const apply = () => {
    onApply(layerIdx, {
      strokeColor: strokeColor || null,
      strokeWidth,
      fillColor: hasFill ? fillColor : null,
      opacity: opacity / 100,
    });
  };

  return (
    <Modal title="Vector Layer Style" onClose={onClose} width={280}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "8px 0" }}>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: V.muted, fontSize: 10, width: 50 }}>Stroke:</span>
          <input type="color" value={strokeColor} onChange={e => setStrokeColor(e.target.value)}
            style={{ width: 28, height: 22, border: `1px solid ${V.panelBorder2}`, borderRadius: 3, cursor: "pointer", padding: 0 }} />
          <span style={{ color: V.ghost, fontSize: 9 }}>{strokeColor}</span>
        </div>

        <AdjRow label="Stroke Width" value={strokeWidth} min={0} max={100} onChange={v => setStrokeWidth(v)} fmt={v => v + "px"} />

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
            <input type="checkbox" checked={hasFill} onChange={e => setHasFill(e.target.checked)}
              style={{ width: 12, height: 12, margin: 0 }} />
            <span style={{ color: V.muted, fontSize: 10 }}>Fill:</span>
          </label>
          {hasFill && (
            <>
              <input type="color" value={fillColor} onChange={e => setFillColor(e.target.value)}
                style={{ width: 28, height: 22, border: `1px solid ${V.panelBorder2}`, borderRadius: 3, cursor: "pointer", padding: 0 }} />
              <span style={{ color: V.ghost, fontSize: 9 }}>{fillColor}</span>
            </>
          )}
        </div>

        <AdjRow label="Opacity" value={opacity} min={0} max={100} onChange={v => setOpacity(v)} fmt={v => v + "%"} />

        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          <OptBtn accent style={{ flex: 1, textAlign: "center" }} onClick={() => { apply(); onClose(); }}>Apply</OptBtn>
          <OptBtn style={{ flex: 1, textAlign: "center" }} onClick={onClose}>Cancel</OptBtn>
        </div>
      </div>
    </Modal>
  );
}
